import React, { useState, useEffect, useCallback } from 'react';
import NoticeListComponent from './Index.component';
import { getNoticeList } from 'api/koach/v2/notice.api';
import { INotice } from 'api/interfaces/bbs.interface';

const NoticeListContainer: React.FC = () => {
    const [noticeList, setNoticeList] = useState<INotice[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    // 첫 번째 페이지 공지사항 목록 조회
    const fetchNoticeList = useCallback(async () => {
        try {
            setIsLoading(true);
            console.log("🔄 첫 번째 페이지 공지사항 목록 조회 시작");

            const result = await getNoticeList(1);
            console.log("📊 첫 번째 페이지 API 응답:", result);

            if (result?.noticeList) {
                setNoticeList(result.noticeList);
                setHasMore(result.pagination?.hasNextPage || false);
                setCurrentPage(1);
                console.log("✅ 첫 번째 페이지 데이터 설정 완료", {
                    itemCount: result.noticeList.length,
                    hasNextPage: result.pagination?.hasNextPage
                });
            } else {
                console.log("⚠️ 공지사항 목록이 비어있음");
                setNoticeList([]);
                setHasMore(false);
            }
        } catch (error) {
            console.error("❌ 공지사항 목록 조회 실패:", error);
            setNoticeList([]);
            setHasMore(false);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // 추가 데이터 로드 (무한 스크롤용)
    const loadMoreData = useCallback(async () => {
        if (isLoadingMore || !hasMore) {
            console.log("🚫 loadMoreData 호출 차단:", { isLoadingMore, hasMore, currentPage });
            return;
        }

        try {
            setIsLoadingMore(true);
            const nextPage = currentPage + 1;
            console.log("📄 다음 페이지 로드 시작:", nextPage);

            const result = await getNoticeList(nextPage);
            console.log("📊 다음 페이지 API 응답:", { page: nextPage, result });

            if (result?.noticeList && result.noticeList.length > 0) {
                setNoticeList(prevList => {
                    const newList = [...prevList, ...result.noticeList];
                    console.log("📊 업데이트된 공지사항 데이터:", {
                        previousCount: prevList.length,
                        newCount: result.noticeList.length,
                        totalCount: newList.length,
                        hasNextPage: result.pagination?.hasNextPage
                    });
                    return newList;
                });
                setCurrentPage(nextPage);
                setHasMore(result.pagination?.hasNextPage || false);
                console.log("✅ 페이지 상태 업데이트:", {
                    newPage: nextPage,
                    hasMore: result.pagination?.hasNextPage || false
                });
            } else {
                console.log("📝 더 이상 로드할 데이터가 없음 (빈 배열 또는 null)");
                setHasMore(false);
            }
        } catch (error) {
            console.error("❌ 추가 데이터 로드 실패:", error);
            setHasMore(false);
        } finally {
            setIsLoadingMore(false);
            console.log("🔄 로딩 상태 해제");
        }
    }, [currentPage, hasMore, isLoadingMore]);

    // 무한 스크롤 이벤트 핸들러
    useEffect(() => {
        let isScrolling = false; // 스크롤 중복 방지 플래그
        let scrollTimeout: NodeJS.Timeout;

        const handleScroll = () => {
            // 이미 스크롤 처리 중이거나 로딩 중이거나 다음 페이지가 없으면 무시
            if (isScrolling || isLoadingMore || !hasMore) {
                console.log("🚫 스크롤 이벤트 무시:", { isScrolling, isLoadingMore, hasMore });
                return;
            }

            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;
            const scrollPosition = scrollTop + windowHeight;
            const triggerPosition = documentHeight - 200; // 200px 여유

            // 스크롤이 하단 근처에 도달했는지 확인
            if (scrollPosition >= triggerPosition) {
                console.log("🔄 스크롤 임계값 도달, 다음 페이지 로드 시작");
                isScrolling = true; // 스크롤 처리 시작

                loadMoreData().finally(() => {
                    // API 호출 완료 후 플래그 리셋 (1초 후)
                    clearTimeout(scrollTimeout);
                    scrollTimeout = setTimeout(() => {
                        isScrolling = false;
                        console.log("🔄 스크롤 플래그 리셋");
                    }, 1000);
                });
            }
        };

        // 스크롤 이벤트 리스너 등록
        window.addEventListener('scroll', handleScroll, { passive: true });
        console.log("👂 스크롤 이벤트 리스너 등록 완료");

        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
            console.log("🗑️ 스크롤 이벤트 리스너 제거");
        };
    }, [isLoadingMore, hasMore, loadMoreData]);

    useEffect(() => {
        fetchNoticeList();
    }, [fetchNoticeList]);

    return (
        <NoticeListComponent
            noticeList={noticeList}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            hasMore={hasMore}
        />
    );
};

export default NoticeListContainer;
